import * as THREE from 'three';
import { ExplosionSystem } from './Explosion';
import { DRONE_HITBOX_RADIUS, TOWER_HITBOX_RADIUS, TOWER_POSITION, TOWER_HEIGHT, RESPAWN_COOLDOWN_MS } from '@shared/constants';

// ============================================================
// Combat system — hit detection, death, respawn, scoring
// ============================================================

const GUN_DAMAGE = 10;
const MISSILE_DAMAGE = 100;
const MAX_HP = 100;

interface CombatTarget {
  readonly mesh: THREE.Object3D;
  readonly name: string;
  readonly hitboxRadius: number;
  readonly detectOnly: boolean; // true = hit detection only, no local damage
  alive: boolean;
  hp: number;
  respawnTimer: number;
  readonly onDeath: () => void;
  readonly onRespawn: () => void;
}

export interface KillFeedEntry {
  readonly message: string;
  readonly color: string;
  age: number;
}

const KILL_FEED_DURATION = 5;

export class CombatSystem {
  private readonly explosions: ExplosionSystem;
  private readonly targets: CombatTarget[] = [];
  readonly killFeed: KillFeedEntry[] = [];
  private readonly _scores = { drone: 0, fighter: 0 };
  private killerName = 'Player';

  constructor(scene: THREE.Scene) {
    this.explosions = new ExplosionSystem(scene);
  }

  setKillerName(name: string): void {
    this.killerName = name;
  }

  addTarget(
    mesh: THREE.Object3D,
    name: string,
    hitboxRadius: number,
    onDeath: () => void,
    onRespawn: () => void,
    detectOnly = false,
  ): void {
    this.targets.push({
      mesh, name, hitboxRadius, detectOnly, alive: true, hp: MAX_HP,
      respawnTimer: 0, onDeath, onRespawn,
    });
  }

  setTargetAlive(mesh: THREE.Object3D, alive: boolean): void {
    const target = this.targets.find((t) => t.mesh === mesh);
    if (target) target.alive = alive;
  }

  removeTarget(mesh: THREE.Object3D): void {
    const idx = this.targets.findIndex((t) => t.mesh === mesh);
    if (idx !== -1) this.targets.splice(idx, 1);
  }

  checkTracerHit(tracerPosition: THREE.Vector3): boolean {
    for (const target of this.targets) {
      if (!target.alive) continue;
      const dist = tracerPosition.distanceTo(target.mesh.position);
      if (dist < target.hitboxRadius + DRONE_HITBOX_RADIUS) {
        // detectOnly: server handles damage, we only report the hit
        if (!target.detectOnly) {
          this.damageTarget(target, GUN_DAMAGE, 'Gun');
        }
        return true;
      }
    }
    return false;
  }

  checkMissileHit(missilePosition: THREE.Vector3, targetMesh: THREE.Object3D): boolean {
    for (const target of this.targets) {
      if (!target.alive) continue;
      if (target.mesh !== targetMesh) continue;
      const dist = missilePosition.distanceTo(target.mesh.position);
      if (dist < target.hitboxRadius + 10) {
        if (!target.detectOnly) {
          this.damageTarget(target, MISSILE_DAMAGE, 'Missile');
        }
        return true;
      }
    }
    return false;
  }

  scoreDronePoint(): void {
    this._scores.drone++;
  }

  update(dt: number): void {
    this.explosions.update(dt);

    for (const target of this.targets) {
      if (!target.alive) {
        target.respawnTimer -= dt * 1000;
        if (target.respawnTimer <= 0) {
          target.alive = true;
          target.hp = MAX_HP;
          target.onRespawn();
        }
      }
    }

    for (let i = this.killFeed.length - 1; i >= 0; i--) {
      this.killFeed[i].age += dt;
      if (this.killFeed[i].age >= KILL_FEED_DURATION) {
        this.killFeed.splice(i, 1);
      }
    }
  }

  isTargetAlive(mesh: THREE.Object3D): boolean {
    const target = this.targets.find((t) => t.mesh === mesh);
    return target ? target.alive : false;
  }

  /** Returns 0-1 health fraction for a target mesh */
  getHealthFraction(mesh: THREE.Object3D): number {
    const target = this.targets.find((t) => t.mesh === mesh);
    return target ? target.hp / MAX_HP : 1;
  }

  private damageTarget(target: CombatTarget, damage: number, weapon: string): void {
    target.hp = Math.max(0, target.hp - damage);

    if (target.hp <= 0) {
      target.alive = false;
      target.respawnTimer = RESPAWN_COOLDOWN_MS;
      this._scores.fighter++;
      this.explosions.spawn(target.mesh.position.clone());
      target.onDeath();
      this.addKillFeedEntry(`${this.killerName} killed ${target.name} with ${weapon}`, '#000');
    }
  }

  private addKillFeedEntry(message: string, color: string): void {
    this.killFeed.unshift({ message, color, age: 0 });
    if (this.killFeed.length > 5) this.killFeed.pop();
  }

  spawnExplosion(position: THREE.Vector3): void {
    this.explosions.spawn(position);
  }

  get scores(): { drone: number; fighter: number } {
    return this._scores;
  }
}
